// src/components/commissioner/MinorLeagueDraftOverride.js
// Commissioner in-season ML draft order override — no draft_status guard
import React, { useState, useEffect } from 'react';
import { AlertCircle, Check, RefreshCw, AlertTriangle } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';

// Exported per Bible §14 frontend testing convention so unit tests can
// import and assert against the canonical method list without inline
// reimplementation.
export const ORDER_METHODS = [
  { value: 'linear', label: 'Linear (same order every round)' },
  { value: 'snake', label: 'Snake (reverse order on even rounds)' },
  { value: 'random', label: 'Random (shuffled)' },
  { value: 'standings', label: 'Standings (worst record picks first)' },
  { value: 'lottery', label: 'Lottery (weighted by standings)' },
  { value: 'manual', label: 'Manual (drag to reorder below)' },
];

// Pure array-reorder helper — extracted from drag-and-drop logic so it
// can be unit-tested directly (JSDOM drag events are unreliable for
// asserting the reorder outcome end-to-end). Returns a new array; never
// mutates input.
export const reorderArray = (arr, fromIdx, toIdx) => {
  if (fromIdx === toIdx) return [...arr];
  const newArr = [...arr];
  const [moved] = newArr.splice(fromIdx, 1);
  newArr.splice(toIdx, 0, moved);
  return newArr;
};

const MinorLeagueDraftOverride = ({ leagueId, teams, league }) => {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Order method selector
  const [selectedMethod, setSelectedMethod] = useState('linear');

  // Manual order: array of team_ids in desired pick order
  const [manualOrder, setManualOrder] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);

  const currentSeason = league?.current_season;

  useEffect(() => {
    if (currentSeason) {
      loadDraft();
    }
  }, [leagueId, currentSeason]);

  const loadDraft = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await leaguesAPI.getMinorLeagueDraft(leagueId);
      if (response && response.draft_id) {
        setDraft(response);
        setSelectedMethod(response.draft_order_method || 'linear');
        // Init manual order from current draft_order
        const order = (response.draft_order || [])
          .sort((a, b) => a.pick_order - b.pick_order)
          .map(entry => entry.team_id);
        setManualOrder(order);
      } else {
        setDraft(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setDraft(null);
      } else {
        setError('Failed to load minor league draft');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOverride = async () => {
    if (!draft) return;
    if (selectedMethod === 'manual' && manualOrder.length !== teams.length) {
      setError('Manual order must include all teams');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = selectedMethod === 'manual' ? manualOrder : null;
      const response = await leaguesAPI.commissionerOverrideMLDraftOrder(leagueId, selectedMethod, payload);
      if (response.success) {
        setMessage(`Draft order overridden to '${selectedMethod}'`);
        setTimeout(() => setMessage(''), 5000);
        await loadDraft();
      } else {
        setError(response.message || 'Failed to override draft order');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to override draft order');
    } finally {
      setSaving(false);
    }
  };

  // Drag-to-reorder for manual method
  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    setManualOrder(reorderArray(manualOrder, dragIndex, index));
    setDragIndex(index);
  };
  const handleDragEnd = () => setDragIndex(null);

  const teamById = (teamId) => teams?.find(t => t.team_id === teamId);

  if (!currentSeason) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-8 text-center`}>
        <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${dynastyTheme.classes.text.warning}`} />
        <p className={dynastyTheme.classes.text.neutralLight}>League current_season not set.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Messages */}
      {message && (
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.success}/20 text-green-400 flex items-center gap-3`}>
          <Check className="w-5 h-5 flex-shrink-0" />
          <span>{message}</span>
        </div>
      )}
      {error && (
        <div className={`p-4 rounded-lg ${dynastyTheme.classes.bg.error}/20 text-red-400 flex items-center gap-3`}>
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Draft state card + refresh */}
      <div className={dynastyTheme.components.card.base}>
        <div className="p-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className={`font-semibold ${dynastyTheme.classes.text.white} mb-1`}>
              {currentSeason} Minor League Draft
            </h3>
            {loading ? (
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>Loading...</p>
            ) : draft ? (
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} space-y-1`}>
                <div>
                  Status:{' '}
                  <span className={`font-medium ${
                    draft.draft_status === 'completed' ? 'text-green-400' :
                    draft.draft_status === 'in_progress' ? 'text-yellow-400' :
                    dynastyTheme.classes.text.white
                  }`}>
                    {draft.draft_status}
                  </span>
                  {' · '}Round {draft.current_round} of {draft.total_rounds}
                  {' · '}Pick {draft.current_pick_in_round} in round
                </div>
                <div>
                  Current method: <span className={dynastyTheme.classes.text.white}>{draft.draft_order_method || 'linear'}</span>
                </div>
              </div>
            ) : (
              <p className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                No minor league draft found for {currentSeason}.
              </p>
            )}
          </div>
          <button
            onClick={loadDraft}
            disabled={loading}
            className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
          >
            <RefreshCw className={`w-3 h-3 mr-1 inline ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {draft && (
        <>
          {/* Current draft order display */}
          <div className={dynastyTheme.components.card.base}>
            <div className="p-6">
              <h4 className={`font-semibold ${dynastyTheme.classes.text.white} mb-3`}>Current Pick Order</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {(draft.draft_order || [])
                  .sort((a, b) => a.pick_order - b.pick_order)
                  .map(entry => (
                    <div
                      key={entry.team_id}
                      className={`${dynastyTheme.classes.bg.darkLighter} rounded px-3 py-2 flex items-center gap-2 text-sm`}
                    >
                      <span className={`font-mono font-bold ${dynastyTheme.classes.text.primary} w-5 text-right`}>
                        {entry.pick_order}
                      </span>
                      <span className={dynastyTheme.classes.text.white}>{entry.team_name}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Warning + Override controls */}
          <div className={`${dynastyTheme.components.card.base} border-l-4 border-red-500`}>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                  <p className="font-semibold text-red-400 mb-1">Commissioner Force Override</p>
                  {draft.draft_status === 'in_progress' ? (
                    <p><strong className="text-red-400">Draft is currently in progress.</strong> Already-recorded picks are <strong className={dynastyTheme.classes.text.white}>never changed</strong> — only the pick order for future picks is affected.</p>
                  ) : (
                    <p>This override bypasses the draft status check. It works for any draft status.</p>
                  )}
                </div>
              </div>

              <div>
                <label className={`block ${dynastyTheme.classes.text.white} font-medium mb-2 text-sm`}>
                  New Order Method
                </label>
                <select
                  value={selectedMethod}
                  onChange={e => setSelectedMethod(e.target.value)}
                  className={`${dynastyTheme.components.input} max-w-sm`}
                  disabled={saving}
                >
                  {ORDER_METHODS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Manual drag-to-reorder */}
              {selectedMethod === 'manual' && (
                <div>
                  <p className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                    Drag teams to set pick order (1 = first pick):
                  </p>
                  <div className="space-y-1 max-w-sm">
                    {manualOrder.map((teamId, idx) => {
                      const team = teamById(teamId);
                      return (
                        <div
                          key={teamId}
                          draggable
                          onDragStart={() => handleDragStart(idx)}
                          onDragOver={e => handleDragOver(e, idx)}
                          onDragEnd={handleDragEnd}
                          className={`flex items-center gap-3 px-3 py-2 rounded cursor-grab active:cursor-grabbing ${
                            dynastyTheme.classes.bg.darkLighter
                          } border ${dragIndex === idx ? 'border-yellow-400' : 'border-transparent'}`}
                        >
                          <span className={`font-mono font-bold text-sm ${dynastyTheme.classes.text.primary} w-5 text-right`}>
                            {idx + 1}
                          </span>
                          <span className={`text-sm ${dynastyTheme.classes.text.white}`}>
                            {team?.team_name || teamId}
                          </span>
                          <span className={`text-xs ${dynastyTheme.classes.text.neutralDark} ml-auto`}>⠿</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <button
                onClick={handleOverride}
                disabled={saving || !draft}
                className={`${dynastyTheme.utils.getComponent('button', 'primary', 'md')} bg-red-600 hover:bg-red-500 border-red-600`}
              >
                {saving ? 'Overriding...' : 'Force Override Draft Order'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Instructions */}
      <div className={`${dynastyTheme.components.card.base} border-l-4 ${dynastyTheme.classes.border.warning}`}>
        <div className="p-6">
          <h4 className={`font-semibold mb-2 ${dynastyTheme.classes.text.warning}`}>How to Use</h4>
          <ul className={`space-y-1 text-sm ${dynastyTheme.classes.text.neutralLight}`}>
            <li>Use this to fix draft order during an active (in-progress) minor league draft</li>
            <li>Select a method and click "Force Override" — already-made picks are never affected</li>
            <li>For pre-draft order setup, use Commissioner Controls &rarr; Draft Setup instead</li>
            <li>Manual mode lets you drag teams into the exact order you want</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MinorLeagueDraftOverride;
